import React, { FC, useRef, useEffect, useState, ReactNode } from "react";
import { format, parse } from "date-fns";
import {
  DateFormats,
  dateFormatter,
  dateValid,
  dateValidMax,
  dateValidMin,
} from "../../utils/validation";
import { useWebTranslation } from "../../translations";
import { Typography } from "../../ui-kit/Typography";
import TextInput from "../../ui-kit/TextInput/TextInput";
import { FlexRow } from "../../ui-kit/Layout/Flex";
import Colors from "../../ui-kit/colors";
import Gutter from "../../ui-kit/Gutter";
import { getDateFormat } from "../../utils/site";
import { DateFormat } from "config/site";

export type DateChange = {
  date: string;
  validation: { isValid: boolean; isValidMin: boolean; isValidMax: boolean };
};

export type DateInputProps = {
  disabled?: boolean;
  value: string;
  onChange: ({ date, validation }: DateChange) => void;
  onFocus?: () => void;
  onBlur?: ({ date, validation }: DateChange) => void;
  min?: Date;
  minError?: ReactNode;
  max?: Date;
  maxError?: ReactNode;
  border?: boolean;
  noValidate?: boolean;
  noIcon?: boolean;
  style?: any;
  autoComplete?: string;
  ariaLabel: string;
  id?: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  required?: boolean;
};

function getDateFormatPlaceholder(dateFormat: DateFormat) {
  switch (dateFormat) {
    case "dd/MM/yyyy": {
      return "dateinput.placeholders.3";
    }

    case "dd.MM.yyyy": {
      return "dateinput.placeholders.2";
    }

    case "yyyy-MM-dd":
    default: {
      return "dateinput.placeholders.1";
    }
  }
}

const DateInput: FC<DateInputProps> = ({
  border = true,
  disabled = false,
  value,
  onChange,
  onBlur,
  min,
  minError,
  max,
  maxError,
  noValidate,
  noIcon,
  style,
  autoComplete,
  onFocus,
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
  required,
}) => {
  const [localValue, setLocalValue] = useState<string>("");
  const { t } = useWebTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<ReactNode>("");

  const dateFormat = getDateFormat();

  const defaultMinError = min
    ? t("date_input.error_min", {
        date: format(min, dateFormat),
      })
    : "";
  const defaultMaxError = max
    ? t("date_input.error_max", {
        date: format(max, dateFormat),
      })
    : "";

  const validate = (
    date: string,
    dateFormat: DateFormats
  ): { isValid: boolean; isValidMin: boolean; isValidMax: boolean } => {
    const isValid = dateValid(date, dateFormat);

    let isValidMin = true;
    let isValidMax = true;

    if (isValid) {
      const parsedDate = parse(date, dateFormat, new Date());
      if (min && !dateValidMin(parsedDate, min)) {
        isValidMin = false;
      }

      if (max && !dateValidMax(parsedDate, max)) {
        isValidMax = false;
      }
    }

    return { isValid, isValidMin, isValidMax };
  };

  const checkValidationError = () => {
    if (value) {
      const validation = validate(value, "yyyy-MM-dd");

      if (!validation.isValid) {
        setError(t("date_input.error_validation"));
      } else if (min && !validation.isValidMin) {
        setError(minError || defaultMinError);
      } else if (max && !validation.isValidMax) {
        setError(maxError || defaultMaxError);
      } else {
        setError("");
      }
    }
  };

  useEffect(() => {
    inputRef.current?.setAttribute("name", "date");
    inputRef.current?.setAttribute("type", "text");
    inputRef.current?.setAttribute("required", "true");
  }, [inputRef]);

  useEffect(() => {
    checkValidationError();
  }, [t]);

  useEffect(() => {
    if (dateValid(value) && value !== localValue) {
      onchangeText(format(new Date(value), dateFormat));
    }
  }, [value]);

  const parseAndValidate = (updatedValue: string) => {
    const date = dateFormatter({
      date: updatedValue,
      prevDate: localValue,
      format: dateFormat,
    });

    setLocalValue(date);

    const validation = validate(date, dateFormat);

    if (min && !validation.isValidMin) {
      setError(minError || defaultMinError);
    } else if (max && !validation.isValidMax) {
      setError(maxError || defaultMaxError);
    } else {
      setError("");
    }

    const isoFormattedDate = dateValid(updatedValue, dateFormat)
      ? format(parse(date, dateFormat, new Date()), "yyyy-MM-dd")
      : "";

    return {
      isoFormattedDate,
      validation,
    };
  };

  const onchangeText = (updatedValue: string) => {
    const { isoFormattedDate, validation } = parseAndValidate(updatedValue);

    onChange({
      date: isoFormattedDate,
      validation,
    });
  };

  return (
    <>
      <FlexRow
        alignItems="center"
        style={
          border
            ? {
                border: `1px solid ${Colors.grey200}`,
                borderRadius: 16,
                padding: 8,
              }
            : {}
        }
      >
        {!noIcon && (
          <span aria-hidden="true" style={{ marginLeft: 8, marginBottom: 2 }}>
            📅
          </span>
        )}
        <TextInput
          required={required}
          nativeRef={inputRef}
          onChangeText={onchangeText}
          value={localValue}
          placeHolder={t(getDateFormatPlaceholder(dateFormat))}
          maxLength={10}
          onFocus={() => {
            parseAndValidate(localValue);
            onFocus && onFocus();
          }}
          onBlur={() => {
            const { isoFormattedDate, validation } =
              parseAndValidate(localValue);

            onBlur && onBlur({ date: isoFormattedDate, validation });
          }}
          disabled={disabled}
          style={{ border: "none", padding: "10px 8px", ...style }}
          autoComplete={autoComplete}
          inputMode="numeric"
          ariaLabel={ariaLabel}
          ariaDescribedBy={
            typeof ariaDescribedBy === "string"
              ? ariaDescribedBy
              : error
              ? "error"
              : undefined
          }
          ariaInvalid={
            Boolean(ariaDescribedBy) ||
            ariaInvalid ||
            Boolean(minError) ||
            Boolean(maxError) ||
            Boolean(error)
          }
        />
      </FlexRow>
      {!noValidate && error && (
        <div id="error">
          <Gutter size={0.5} />
          <Typography color={"error"}>{error}</Typography>
        </div>
      )}
    </>
  );
};

interface StackedTestInputProps extends DateInputProps {
  first?: boolean;
  last?: boolean;
}

export const StackedDateInput = ({
  first,
  last,
  ...rest
}: StackedTestInputProps) => {
  const style = {
    border: `1px solid ${Colors.grey200}`,
    borderRadius: 0,
    borderTopWidth: first ? 1 : 0,
    borderTopLeftRadius: first ? 24 : 0,
    borderTopRightRadius: first ? 24 : 0,
    borderBottomLeftRadius: last ? 24 : 0,
    paddingLeft: 20,
  };
  return <DateInput {...rest} style={style} noIcon border={false} />;
};

DateInput.defaultProps = {
  min: undefined,
  max: undefined,
  onFocus: () => undefined,
  disabled: false,
  border: true,
  noValidate: false,
};

export default DateInput;
